import { useQuery } from '@tanstack/react-query';
import { ActivityLogs, type ActivityLogEntry } from './api';

/**
 * Son 7 gün için günlük çağrı sayısı (call_answered + call_started).
 * Index 0 = 6 gün önce, index 6 = bugün.
 *
 * Backend'den çağrı endpoint olmadığı için activity_logs'tan derliyoruz.
 * limit=500 yeterli — kullanıcı haftada 500 event'in altında log üretir
 * normalde. Daha eski/uzun dönem ileride backend'de cumulative endpoint
 * eklenince oradan alınır.
 */
export interface WeeklyTrend {
  days: { date: string; calls: number; talkSec: number }[];
  /** Sparkline için sadece call count array'i */
  callsByDay: number[];
  /** Bugünün ortalamadan farkı (% olarak, +30 = %30 fazla) */
  todayDeltaPct: number | null;
}

function startOfDayMs(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function buildLast7Days(now: number): { date: string; ts: number }[] {
  const today = startOfDayMs(now);
  const out: { date: string; ts: number }[] = [];
  for (let i = 6; i >= 0; i--) {
    const ts = today - i * 86_400_000;
    const d = new Date(ts);
    const date = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ date, ts });
  }
  return out;
}

function computeTrend(logs: ActivityLogEntry[]): WeeklyTrend {
  const now = Date.now();
  const buckets = buildLast7Days(now);
  // Map ts → {calls, talkSec}
  const stats = new Map<number, { calls: number; talkSec: number }>();
  for (const b of buckets) stats.set(b.ts, { calls: 0, talkSec: 0 });

  for (const l of logs) {
    const ts = new Date(l.createdAt).getTime();
    const dayTs = startOfDayMs(ts);
    if (!stats.has(dayTs)) continue;
    const meta = (l.meta ?? {}) as Record<string, any>;
    if (l.type === 'call_answered' || l.type === 'call_started') {
      stats.get(dayTs)!.calls += 1;
    } else if (l.type === 'call_ended') {
      const ms = typeof meta.duration_ms === 'number' ? meta.duration_ms : 0;
      if (ms > 0) stats.get(dayTs)!.talkSec += Math.round(ms / 1000);
    }
  }

  const days = buckets.map((b) => {
    const s = stats.get(b.ts)!;
    return { date: b.date, calls: s.calls, talkSec: s.talkSec };
  });
  const callsByDay = days.map((d) => d.calls);
  const today = callsByDay[callsByDay.length - 1];
  const past6 = callsByDay.slice(0, 6).filter((v) => v > 0);
  const past6Avg = past6.length > 0 ? past6.reduce((a, b) => a + b, 0) / past6.length : 0;
  const todayDeltaPct = past6Avg > 0 ? Math.round(((today - past6Avg) / past6Avg) * 100) : null;

  return { days, callsByDay, todayDeltaPct };
}

export function useWeeklyTrend(): WeeklyTrend {
  const { data } = useQuery({
    queryKey: ['weekly-trend'],
    queryFn: () => ActivityLogs.listMine(500),
    refetchInterval: 60_000, // dakikada bir
    staleTime: 30_000,
  });
  if (!data) return { days: [], callsByDay: [], todayDeltaPct: null };
  return computeTrend(data);
}
